import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Building2, Pencil, Palette, Sparkles, ChevronRight } from "lucide-react";

const Brands = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consolidating, setConsolidating] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAgency = user?.plan === "agency";
  const singleBrandPlan = !isAgency;
  const overLimit = singleBrandPlan && brands.length > 1;
  const atLimit = singleBrandPlan && brands.length >= 1;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/brands");
      setBrands(data);
    } catch (e) {
      toast.error("Errore nel caricamento dei brand");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Eliminare questo brand profile?")) return;
    try {
      await api.delete(`/brands/${id}`);
      setBrands((b) => b.filter((x) => x.id !== id));
      toast.success("Brand eliminato");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const onKeepOnlyThis = async (keepId) => {
    const target = brands.find((b) => b.id === keepId);
    if (!target) return;
    if (!window.confirm(`Mantenere solo "${target.name}" e cancellare gli altri brand? Questa operazione non si può annullare.`)) {
      return;
    }
    setConsolidating(true);
    try {
      await api.post("/brands/consolidate", { keep_id: keepId });
      toast.success(`Brand consolidato su "${target.name}"`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore consolidamento");
    } finally {
      setConsolidating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400] mb-3">
              {isAgency ? "· Agency · Brand Profiles" : "· Il tuo Brand"}
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter text-white">
              {isAgency ? "I tuoi clienti." : "Il tuo brand."}
            </h1>
            <p className="text-zinc-400 font-body mt-2">
              {brands.length === 0
                ? (isAgency
                    ? "Crea un brand profile per riutilizzare logo, colori e tono di voce su ogni reel."
                    : "Crea il tuo brand una sola volta. Logo, colori, tono di voce — tutto pronto per generare reel in un click.")
                : `${brands.length} brand profile · piano ${user?.plan?.toUpperCase() || "FREE"}`}
            </p>
          </div>
          <div className="flex gap-3">
            {!isAgency && (
              <Link
                to="/pricing"
                data-testid="brands-upgrade"
                className="inline-flex items-center gap-2 px-4 py-3 border border-zinc-800 hover:border-[#FF4400] hover:text-[#FF4400] text-zinc-400 font-body rounded-sm transition-all"
              >
                <Sparkles className="w-4 h-4" />
                Upgrade
              </Link>
            )}
            {(!atLimit || isAgency) && (
              <button
                onClick={() => navigate("/brands/new")}
                data-testid="brands-create"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
              >
                <Plus className="w-4 h-4" />
                {isAgency ? "Nuovo Brand" : "Crea il mio brand"}
              </button>
            )}
          </div>
        </div>

        {overLimit && (
          <div
            data-testid="brands-consolidate-banner"
            className="mb-8 border border-[#FF4400] bg-[#FF4400]/10 rounded-sm p-5 md:p-6"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4400] mb-2">
              · Azione richiesta
            </div>
            <h2 className="font-display font-black text-xl md:text-2xl tracking-tight text-white mb-2">
              Hai {brands.length} brand profile, ma il piano <span className="text-[#FF4400]">{user?.plan?.toUpperCase() || "PRO"}</span> ne consente 1 solo.
            </h2>
            <p className="text-sm text-zinc-300 font-body">
              Scegli il brand che vuoi mantenere cliccando "Tieni questo" sulla card. Gli altri verranno eliminati.
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-3 text-zinc-500 py-20">
            <span className="w-2 h-2 rounded-full bg-[#FF4400] pulse-dot" />
            <span className="font-mono text-xs tracking-widest uppercase">Loading brands...</span>
          </div>
        ) : brands.length === 0 ? (
          <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-12 md:p-20 text-center" data-testid="brands-empty-state">
            <div className="w-16 h-16 mx-auto mb-6 rounded-sm bg-[#FF4400]/10 border border-[#FF4400]/40 flex items-center justify-center">
              <Building2 className="w-7 h-7 text-[#FF4400]" />
            </div>
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter text-white mb-3">
              Nessun brand profile.
            </h2>
            <p className="text-zinc-400 font-body max-w-md mx-auto mb-8">
              Salva clienti o brand con logo, colori e tono. Generi reel in un click con tutto pre-compilato.
            </p>
            <button
              onClick={() => navigate("/brands/new")}
              data-testid="brands-empty-cta"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF4400] hover:bg-[#FF6633] text-white font-display font-bold rounded-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Crea il primo brand
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {brands.map((b) => (
              <div
                key={b.id}
                data-testid={`brand-card-${b.id}`}
                className="group border border-zinc-800 bg-zinc-950 rounded-sm p-5 hover:border-[#FF4400] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-14 h-14 rounded-sm flex items-center justify-center font-display font-black text-white text-lg overflow-hidden border"
                    style={{
                      backgroundColor: b.color_primary || "#FF4400",
                      borderColor: b.color_primary || "#FF4400",
                    }}
                  >
                    {b.logo_base64 ? (
                      <img src={`data:image/png;base64,${b.logo_base64}`} alt={b.name} className="w-full h-full object-cover" />
                    ) : (
                      b.name?.[0]?.toUpperCase() || "B"
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-white truncate">{b.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                      {b.industry}
                    </div>
                  </div>
                  <button
                    onClick={(e) => onDelete(b.id, e)}
                    data-testid={`brand-delete-${b.id}`}
                    className="opacity-0 group-hover:opacity-100 w-8 h-8 rounded-sm border border-zinc-800 text-zinc-500 hover:text-[#FF4400] hover:border-[#FF4400] flex items-center justify-center transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-sm text-zinc-400 line-clamp-2 mb-4 font-body">{b.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1">
                      <div className="w-4 h-4 rounded-full border border-zinc-900" style={{ backgroundColor: b.color_primary }} />
                      <div className="w-4 h-4 rounded-full border border-zinc-900" style={{ backgroundColor: b.color_secondary }} />
                    </div>
                    <Palette className="w-3 h-3 text-zinc-600" />
                  </div>
                  <Link
                    to={`/brands/${b.id}`}
                    data-testid={`brand-edit-${b.id}`}
                    className="inline-flex items-center gap-1 text-xs text-[#FF4400] hover:text-[#FF6633] font-bold font-mono uppercase tracking-widest"
                  >
                    <Pencil className="w-3 h-3" />
                    Edit
                    <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
                {overLimit && (
                  <button
                    onClick={() => onKeepOnlyThis(b.id)}
                    disabled={consolidating}
                    data-testid={`brand-keep-${b.id}`}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-40 text-white font-display font-bold rounded-sm text-sm transition-colors"
                  >
                    Tieni questo
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Brands;
